import { Center, Heading, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LibraryElementTypeDto, useListLibraryElementsQuery } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { AwardCriterionLibraryTab } from "./AwardCriterionLibraryTab";
import { AwardSubCriterionLibraryTab } from "./AwardSubCriterionLibraryTab";
import { MinimumRequirementLibraryTab } from "./MinimumRequirementLibraryTab";
import { QualificationLibraryTab } from "./QualificationLibraryTab";

export const Kravbibliotek = () => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const authState = useLoggedInWithOrgContextState();
  const orgName = authState.selectedOrg.name;
  const canManageOrg = authState.selectedOrg.canEditDocumentTemplates;
  const isSystemAdmin = authState.me.isSystemAdmin;
  const { trigger } = useApiError();
  const { data, isLoading, error } = useListLibraryElementsQuery({});
  const elements = data?.elements ?? [];
  useEffect(() => {
    if (error) trigger(error);
  }, [error, trigger]);
  const countOf = (type: LibraryElementTypeDto) => elements.filter((e) => e.content.type === type).length;

  const addRefusal = authState.selectedOrg.subscription?.hasBuyerAccess !== false
    ? undefined
    : t("NotInPlan");
  const panelProps = { elements, orgName, canManageOrg, addRefusal, isSystemAdmin };
  const tabs = [
    {
      id: "minimum",
      label: `${t("Minimum requirements")} (${countOf(LibraryElementTypeDto.Minimum)})`,
      panel: <MinimumRequirementLibraryTab {...panelProps} />,
    },
    ...(runsPublicProcurements
      ? [
          {
            id: "selection",
            label: `${t("Qualification requirements")} (${countOf(LibraryElementTypeDto.Selection)})`,
            panel: <QualificationLibraryTab {...panelProps} />,
          },
        ]
      : []),
    {
      id: "award",
      label: `${terms.awardCriteria} (${countOf(LibraryElementTypeDto.Award)})`,
      panel: <AwardCriterionLibraryTab {...panelProps} />,
    },
    {
      id: "awardSub",
      label: `${terms.awardSubCriteria} (${countOf(LibraryElementTypeDto.AwardSubCriterion)})`,
      panel: <AwardSubCriterionLibraryTab {...panelProps} />,
    },
  ];

  return (
    <VStack align="stretch" spacing="5">
      <Heading size="lg" color="smText">
        {t("Requirement library")}
      </Heading>
      {isLoading && !data ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="teal" isLazy>
          <TabList gap="2">
            {tabs.map(({ id, label }) => (
              <Tab key={id}>{label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map(({ id, panel }) => (
              <TabPanel key={id} px="0" pt="5">
                {panel}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </VStack>
  );
};
